export const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://nexa-backend-red.vercel.app';

const TOKEN_KEY = 'nexa_admin_token';

/**
 * Mirrors AdminRole on the server. A marketer signs into the same console but
 * only reaches the Partner Network screens.
 */
export type AdminRole = 'superadmin' | 'admin' | 'marketer';

export interface Admin {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  title: string;
  phone: string;
  zohoEmail: string;
  /** True when the server holds an encrypted Zoho password for this admin. */
  hasSavedMailPassword: boolean;
  mustChangePassword: boolean;
}

export const getToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const clearToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

/** Adds the bearer token to a request. */
export const authHeaders = (): Record<string, string> => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * fetch wrapper for admin endpoints. A 401 means the session is gone, so the
 * token is dropped immediately rather than left to fail on every later call.
 */
export const authFetch = async (path: string, init: RequestInit = {}): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(init.headers || {}),
    },
  });

  const data = await response.json().catch(() => ({}));

  if (response.status === 401) {
    clearToken();
    throw new Error(data.error || 'Your session has expired. Sign in again.');
  }

  if (!response.ok) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
};

export const login = async (
  email: string,
  password: string
): Promise<{ token: string; admin: Admin }> => {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || 'Could not sign in');
  }

  return data;
};

export const fetchMe = async (): Promise<Admin> => {
  const data = await authFetch('/api/auth/me');
  return data.admin;
};

export interface ProfileUpdate {
  name?: string;
  title?: string;
  phone?: string;
  zohoEmail?: string;
  zohoPassword?: string;
  clearZohoPassword?: boolean;
}

export const updateProfile = async (update: ProfileUpdate): Promise<Admin> => {
  const data = await authFetch('/api/auth/profile', {
    method: 'PATCH',
    body: JSON.stringify(update),
  });
  return data.admin;
};

export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  await authFetch('/api/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
};

export const listAdmins = async (): Promise<Admin[]> => {
  const data = await authFetch('/api/auth/admins');
  return data.admins;
};

export interface CreateAdminResult {
  admin: Admin;
  /** False when the account was created but the invite email did not go out. */
  inviteSent: boolean;
  inviteError?: string;
}

export const createAdmin = async (payload: {
  email: string;
  password: string;
  name: string;
  title?: string;
  phone?: string;
  role?: AdminRole;
  sendInvite?: boolean;
}): Promise<CreateAdminResult> => {
  const data = await authFetch('/api/auth/admins', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return { admin: data.admin, inviteSent: data.inviteSent, inviteError: data.inviteError };
};

export const deleteAdmin = async (id: string): Promise<void> => {
  await authFetch(`/api/auth/admins/${id}`, { method: 'DELETE' });
};
